<script setup lang="ts">
import { computed, ref } from 'vue'
import { Menu, SwitchButton, Warning } from '@element-plus/icons-vue'
import { useRoute, useRouter } from 'vue-router'
import { adminNavigationGroups } from '../navigation'
import { useSessionStore } from '../stores/session'
import { performAdminSafeExit } from '../security/safeExit'
import { adminAuthApi } from '../api/adminAuth'
import { resetAdminAuthState } from '../router'

const route = useRoute()
const router = useRouter()
const session = useSessionStore()
const mobileNavOpen = ref(false)
const safeEntry = computed(() => route.meta.safeEntry === true)
const exiting = ref(false)
const groups = computed(() => adminNavigationGroups())

async function safeExit() {
  if (exiting.value) return
  exiting.value = true
  mobileNavOpen.value = false
  await adminAuthApi.logout().catch(() => undefined)
  resetAdminAuthState()
  await performAdminSafeExit(session, router)
  exiting.value = false
}
</script>

<template>
  <div class="admin-shell">
    <header class="top-bar">
      <el-button class="mobile-nav-trigger" :icon="Menu" circle aria-label="打开导航" @click="mobileNavOpen = true" />
      <div class="brand-lockup">
        <span class="brand-mark" aria-hidden="true">B</span>
        <strong>BondPet 运营台</strong>
      </div>
      <div v-if="!safeEntry" class="environment-status" role="status">
        <el-icon><Warning /></el-icon>
        <span>{{ session.environment }}</span>
        <span>{{ session.dataScope }}</span>
        <span>权限版本 {{ session.permissionEpoch }}</span>
      </div>
      <el-tooltip v-if="!safeEntry" content="安全退出" placement="bottom">
        <el-button class="safe-exit-button" :icon="SwitchButton" aria-label="安全退出" :loading="exiting" @click="safeExit"><span>安全退出</span></el-button>
      </el-tooltip>
    </header>

    <aside v-if="!safeEntry" class="side-nav" aria-label="主导航">
      <nav>
        <section v-for="[group, contracts] in groups" :key="group" class="nav-group">
          <h2>{{ group }}</h2>
          <router-link
            v-for="contract in contracts"
            :key="contract.id"
            :to="contract.path"
            :aria-current="route.name === contract.id ? 'page' : undefined"
          >
            {{ contract.title }}
          </router-link>
        </section>
      </nav>
    </aside>

    <el-drawer v-if="!safeEntry" v-model="mobileNavOpen" direction="ltr" size="min(88vw, 320px)" title="主导航">
      <nav class="drawer-nav">
        <template v-for="[group, contracts] in groups" :key="group">
          <h2>{{ group }}</h2>
          <router-link v-for="contract in contracts" :key="contract.id" :to="contract.path" @click="mobileNavOpen = false">
            {{ contract.title }}
          </router-link>
        </template>
      </nav>
    </el-drawer>

    <main id="main-content" class="main-content" :class="{ 'safe-entry-main': safeEntry }" tabindex="-1">
      <router-view />
    </main>
  </div>
</template>
